import React, { useEffect, useState } from 'react';
import {
  Button,
  IconButton,
  // TableBody,
  // Table,
  // TableHead,
  // TableRow,
  // TableCell,
  TextField as TextFieldBase,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '../../../../../components/Generic/StyledComponent';
import Modal from 'src/components/common/modal';
import { Formik, Field } from 'formik';
import * as yup from 'yup';
import LoaderButton from '../loaderButton';
import { MutualAid } from 'src/services/api';
import useCredentials from 'src/hooks/useCredentials';
import { USER_ROLES } from 'src/constants';
import { newHasRole } from '../..';
import { height } from '@mui/system';

const useStyles = makeStyles({
  table: {
    maxHeight: '50%',
    marginBottom: 40,
    overflowY: 'scroll'
  },
  headerText: {
    color: 'rgba(107, 119, 140, 1)'
  },
  formControl: {
    width: '100%'
  }
});

const FIELDS = {
  REASON: 'comments',
  OFFICER: 'referee',
  RECTIFICATIONCATEGORY: 'rectificationCategory',
  CUSTOM_REASON: 'customReason'
};

const VALIDATION_SCHEMA = yup.object({
  [FIELDS.REASON]: yup.string().required('Required'),
  [FIELDS.OFFICER]: yup.string().required('Required'),
  [FIELDS.RECTIFICATIONCATEGORY]: yup.string().required('Required'),
  [FIELDS.CUSTOM_REASON]: yup.string().when(FIELDS.REASON, {
    is: 'Others',
    then: (schema) =>
      schema
        .required('Required')
        .min(15, 'Minimum 15 characters required')
        .max(500, 'Maximum 500 characters allowed'),
    otherwise: (schema) => schema.notRequired()
  })
});

function createData(name, calories, fat, carbs, protein) {
  return { name, calories, fat, carbs, protein };
}

const rows = [
  createData('Frozen yoghurt', 159, 6.0, 24, 4.0),
  createData('Ice cream sandwich', 237, 9.0, 37, 4.3),
  createData('Eclair', 262, 16.0, 24, 6.0),
  createData('Cupcake', 305, 3.7, 67, 4.3),
  createData('Gingerbread', 356, 16.0, 49, 3.9)
];

function RectificationModal({
  headerLabel,
  open,
  handleClose,
  onCancelPress,
  onSuccessPress,
  resubmitOfficers = [],
  rectificationCategory = []
}) {
  // const classes = useStyles();
  const { accessToken } = useCredentials();

  const [categories, setCategories] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  const getReviewerRole = () => {
    if (newHasRole([USER_ROLES.IO_Reviewer])) {
      return USER_ROLES.IO_Reviewer;
    } else if (newHasRole([USER_ROLES.DOC_Reviewer])) {
      return USER_ROLES.DOC_Reviewer;
    } else if (newHasRole([USER_ROLES.FO_Reviewer])) {
      return USER_ROLES.FO_Reviewer;
    } else if (newHasRole([USER_ROLES.Claim_Manager_Reviewer])) {
      return USER_ROLES.Claim_Manager_Reviewer;
    } else if (newHasRole([USER_ROLES.Higher_Level_Reviewer])) {
      return USER_ROLES.Higher_Level_Reviewer;
    }
  };

  const fetchCategories = async (params = {}) => {
    setLoadingCategories(true);
    try {
      const reviewer = getReviewerRole();
      const data = {
        status: 'RESUBMIT',
        page: params.nextPage || 0,
        size: 50,
        searchTerm: params.search || ''
      };

      const response = await MutualAid.getNonRegisterCategory({
        accessToken,
        reviewer,
        data
      });

      if (response.code === 200) {
        const transformedData = (response?.data || []).map((item) => ({
          id: item?.id || item,
          name: item?.name || item
        }));
        setCategories(transformedData);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchReasons = async (params = {}) => {
    if (!params.categoryId) {
      setReasons([]);
      return;
    }

    setLoadingReasons(true);
    try {
      const reviewer = getReviewerRole();
      const data = {
        status: 'RESUBMIT',
        page: params.nextPage || 0,
        size: 50,
        categoryId: params.categoryId
      };

      const response = await MutualAid.getNonRegisterCategoryReason({
        accessToken,
        reviewer,
        data
      });

      if (response.code === 200) {
        const transformedData = (response?.data || []).map((item) => ({
          id: item,
          name: item
        }));
        setReasons(transformedData);
      }
    } catch (error) {
      console.error('Error fetching reasons:', error);
      setReasons([]);
    } finally {
      setLoadingReasons(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
      setSelectedCategoryId('');
      setReasons([]);
    }
  }, [open]);

  useEffect(() => {
    if (selectedCategoryId) {
      fetchReasons({ categoryId: selectedCategoryId });
    } else {
      setReasons([]);
    }
  }, [selectedCategoryId]);

  const handleCategoryChange = (name, setFieldValue) => {
    const selectedCategory = categories.find((item) => item.name == name);
    setSelectedCategoryId(selectedCategory?.id || '');
    setFieldValue(FIELDS.RECTIFICATIONCATEGORY, name);
    setFieldValue(FIELDS.REASON, '');
    setFieldValue(FIELDS.CUSTOM_REASON, '');
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      withoutScroll
      childrenStyle={{ padding: 24 }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 20
        }}
      >
        <Typography>Rectification</Typography>
        <IconButton style={{ padding: 5 }} onClick={handleClose} size="large">
          <CloseIcon />
        </IconButton>
      </div>

      <Formik
        initialValues={{
          [FIELDS.REASON]: '',
          [FIELDS.OFFICER]: '',
          [FIELDS.RECTIFICATIONCATEGORY]: '',
          [FIELDS.CUSTOM_REASON]: ''
        }}
        onSubmit={(values) => {
          const payload = {
            referee: values.referee,
            rectificationCategory: values.rectificationCategory,
            comments:
              values.comments === 'Others'
                ? values.customReason
                : values.comments
          };
          if (values.comments === 'Others') {
            payload.categoryId = selectedCategoryId;
          }
          onSuccessPress(payload);
        }}
        validationSchema={VALIDATION_SCHEMA}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <>
            <Field
              name={FIELDS.RECTIFICATIONCATEGORY}
              component={({ field, form }) => (
                <SelectField
                  field={field}
                  form={form}
                  label="Rectification Category"
                  options={categories}
                  loading={loadingCategories}
                  onChange={(value) =>
                    handleCategoryChange(value, form.setFieldValue)
                  }
                />
              )}
            />
            <Divider
              style={{
                width: '100%',
                marginBottom: 24,
                borderStyle: 'dashed'
              }}
            />
            <Field
              name={FIELDS.REASON}
              component={({ field, form }) => (
                <SelectField
                  field={field}
                  form={form}
                  label="Reason for Rectification"
                  options={reasons}
                  loading={loadingReasons}
                  disabled={!selectedCategoryId}
                  onChange={(value) => {
                    form.setFieldValue(FIELDS.REASON, value);
                    if (value !== 'Others') {
                      form.setFieldValue(FIELDS.CUSTOM_REASON, '');
                    }
                  }}
                />
              )}
            />

            {values[FIELDS.REASON] === 'Others' && (
              <>
                <Divider
                  style={{
                    width: '100%',
                    marginBottom: 24,
                    borderStyle: 'dashed'
                  }}
                />
                <Field
                  name={FIELDS.CUSTOM_REASON}
                  component={TextField}
                  label="Remarks"
                />
              </>
            )}
            <Divider
              style={{ width: '100%', marginBottom: 24, borderStyle: 'dashed' }}
            />
            <Field
              name={FIELDS.OFFICER}
              component={SelectField}
              options={resubmitOfficers}
              label="Send Rectification Request To"
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="outlined"
                style={{
                  marginRight: 10,
                  textTransform: 'capitalize'
                }}
                onClick={onCancelPress || handleClose}
              >
                Cancel
              </Button>
              <LoaderButton
                variant="contained"
                style={{
                  textTransform: 'capitalize',
                  backgroundColor: 'rgba(236, 176, 45, 1)'
                }}
                onClick={handleSubmit}
              >
                Send Request
              </LoaderButton>
            </div>
          </>
        )}
      </Formik>
    </Modal>
  );
}

function TextField({
  field: { onChange, value, name },
  form: { errors },
  label
}) {
  return (
    <>
      <TextFieldBase
        name={name}
        label={`${label}`}
        variant="outlined"
        style={{ width: '100%' }}
        value={value}
        onChange={onChange}
        error={!!errors[name]}
        multiline
        rows={3}
      />
      <FormHelperText error>{errors[name]}</FormHelperText>
    </>
  );
}

function SelectField({
  field: { onChange, value, name },
  form: { errors, touched },
  options = [],
  label,
  loading = false,
  disabled = false,
  onChange: customOnChange
}) {
  const handleChange = (event) => {
    if (customOnChange) {
      customOnChange(event.target.value);
    }
    onChange(event);
  };
  return (
    <>
      <FormControl
        variant="outlined"
        fullWidth
        disabled={disabled}
        error={touched?.[name] && Boolean(errors?.[name])}
      >
        <InputLabel>{label}</InputLabel>
        <Select
          name={name}
          value={value}
          onChange={handleChange}
          label={label}
          MenuProps={{
            PaperProps: {
              className: 'custom-select-menu',
              style: {
                maxHeight: 300,
                overflowY: 'auto'
              }
            }
          }}
        >
          <MenuItem value="">
            <em>Select {label}</em>
          </MenuItem>
          {loading ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : (
            options.map((item) => (
              <MenuItem
                key={item?.id || item}
                value={item?.name || item}
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}
              >
                {item?.name || item}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      <FormHelperText error>{errors[name]}</FormHelperText>
    </>
  );
}

export default RectificationModal;
